<template>
    <Teleport to="body">
        <div v-if="toasts.length > 0"
            class="fixed top-4 right-4 left-4 sm:left-auto z-[9999] space-y-2 max-w-md sm:w-80 md:w-96" aria-live="polite"
            aria-label="Notificações" role="region">
            <TransitionGroup name="toast" tag="div" class="space-y-2">
                <div v-for="toast in toasts" :key="toast.id" :class="[
                    'w-full bg-white shadow-lg rounded-xl pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden',
                    'transform transition-all duration-300 ease-in-out',
                    getBorderClass(toast.type)
                ]" role="alert" :aria-describedby="`toast-message-${toast.id}`">
                    <div class="p-4">
                        <div class="flex items-start">
                            <div class="flex-shrink-0">
                                <component :is="getIcon(toast.type)" :class="getIconClass(toast.type)" />
                            </div>
                            <div class="ml-3 flex-1 pt-0.5 min-w-0">
                                <p v-if="toast.title" class="text-sm font-medium text-gray-900 break-words">
                                    {{ toast.title }}
                                </p>
                                <p v-if="toast.message" :id="`toast-message-${toast.id}`"
                                    class="text-sm text-gray-600 break-words" :class="{ 'mt-1': toast.title }">
                                    {{ toast.message }}
                                </p>
                            </div>
                            <div class="ml-4 flex-shrink-0 flex">
                                <button @click="removeToast(toast.id)"
                                    class="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pdca-500 transition-colors duration-200"
                                    :aria-label="`Fechar notificação: ${toast.title || toast.message}`">
                                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd"
                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                            clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Progress bar -->
                    <div v-if="toast.duration && toast.duration > 0" class="h-1 bg-gray-200">
                        <div :class="[
                            'h-full transition-all ease-linear',
                            getProgressBarClass(toast.type)
                        ]" :style="{
                width: `${getProgress(toast)}%`,
                transitionDuration: `${toast.duration}ms`
            }"></div>
                    </div>
                </div>
            </TransitionGroup>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { h, onMounted, onUnmounted } from 'vue'
import { useToast, type ToastMessage } from '@/composables/useToast'

// Use the composable
const { toasts, removeToast, success, error, warning, info, apiSuccess, apiError } = useToast()

// Icons as render functions
const SuccessIcon = () => h('svg', {
    class: 'w-5 h-5',
    fill: 'currentColor',
    viewBox: '0 0 20 20'
}, [
    h('path', {
        'fill-rule': 'evenodd',
        d: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
        'clip-rule': 'evenodd'
    })
])

const ErrorIcon = () => h('svg', {
    class: 'w-5 h-5',
    fill: 'currentColor',
    viewBox: '0 0 20 20'
}, [
    h('path', {
        'fill-rule': 'evenodd',
        d: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
        'clip-rule': 'evenodd'
    })
])

const WarningIcon = () => h('svg', {
    class: 'w-5 h-5',
    fill: 'currentColor',
    viewBox: '0 0 20 20'
}, [
    h('path', {
        'fill-rule': 'evenodd',
        d: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
        'clip-rule': 'evenodd'
    })
])

const InfoIcon = () => h('svg', {
    class: 'w-5 h-5',
    fill: 'currentColor',
    viewBox: '0 0 20 20'
}, [
    h('path', {
        'fill-rule': 'evenodd',
        d: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z',
        'clip-rule': 'evenodd'
    })
])

function getIcon(type: ToastMessage['type']) {
    const icons = {
        success: SuccessIcon,
        error: ErrorIcon,
        warning: WarningIcon,
        info: InfoIcon
    }
    return icons[type]
}

function getIconClass(type: ToastMessage['type']): string {
    const classes = {
        success: 'text-green-500',
        error: 'text-red-500',
        warning: 'text-amber-500',
        info: 'text-blue-500'
    }
    return classes[type]
}

function getProgressBarClass(type: ToastMessage['type']): string {
    const classes = {
        success: 'bg-green-500',
        error: 'bg-red-500',
        warning: 'bg-amber-500',
        info: 'bg-blue-500'
    }
    return classes[type]
}

function getBorderClass(type: ToastMessage['type']): string {
    const classes = {
        success: 'border-l-4 border-green-500',
        error: 'border-l-4 border-red-500',
        warning: 'border-l-4 border-amber-500',
        info: 'border-l-4 border-blue-500'
    }
    return classes[type]
}

function getProgress(toast: ToastMessage): number {
    if (!toast.duration || toast.duration <= 0) return 100

    // This is a simplified version - in a real implementation you'd track creation time
    return 100
}

// Global toast instance
const toast = {
    success: (message: string, title?: string) => success(title || 'Sucesso', message),
    error: (message: string, title?: string) => error(title || 'Erro', message),
    warning: (message: string, title?: string) => warning(title || 'Atenção', message),
    info: (message: string, title?: string) => info(title || 'Informação', message),
    apiSuccess,
    apiError
}

// Global toast instance
declare global {
    interface Window {
        $toast?: typeof toast
    }
}

onMounted(() => {
    window.$toast = toast
})

onUnmounted(() => {
    window.$toast = undefined
})
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from {
    opacity: 0;
    transform: translateX(100%);
}

.toast-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.toast-move {
    transition: transform 0.3s ease;
}
</style>
